// Angular Files
import {
    Component,
    DOCUMENT,
    ElementRef,
    HostListener,
    Inject,
    NgZone,
    OnDestroy,
    effect,
    signal,
    viewChild
} from '@angular/core';

// Angular Material Files
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

// Component Files
import { GuideStep } from './engagement.models';
import { Box, NARROW_VIEWPORT, computeCardPosition } from './guide-position';
import { TellerOnlineGuideService } from './guide.service';

const TICK_MS = 200;
/** How long a target may be absent before the step is skipped or centred. */
const LOCATE_MS = 600;
const SPOTLIGHT_PADDING = 6;

type Mode = 'locating' | 'spotlight' | 'centered';

@Component({
    selector: 'teller-online-guide-overlay',
    imports: [MatButtonModule, MatIconModule],
    templateUrl: './guide-overlay.component.html',
    styleUrls: ['./guide-overlay.component.scss']
})
export class TellerOnlineGuideOverlayComponent implements OnDestroy {
    readonly mode = signal<Mode>('locating');

    readonly spotlight = signal<Box | null>(null);

    readonly cardPosition = signal<{ top: number; left: number } | null>(null);

    /** The step on screen; swapped together with the spotlight so text and move stay in sync. */
    readonly shown = signal<{ index: number; step: GuideStep } | null>(null);

    private _card = viewChild<ElementRef<HTMLElement>>('card');

    private _timer: ReturnType<typeof setInterval> | null = null;

    private _stepKey = '';

    private _missingMs = 0;

    private _lastIndex = 0;

    private _returnFocus: HTMLElement | null = null;

    constructor(
        public guideService: TellerOnlineGuideService,
        @Inject(DOCUMENT) private document: Document,
        private ngZone: NgZone
    ) {
        effect(() => {
            if (this.guideService.active()) {
                this._begin();
                // Locate the new step now rather than on the next tick
                this.ngZone.runOutsideAngular(() => setTimeout(() => this._tick()));
            } else {
                this._end();
            }
        });
    }

    public get isLast(): boolean {
        const active = this.guideService.active();
        const shown = this.shown();

        return !!active && !!shown && shown.index === active.guide.steps.length - 1;
    }

    @HostListener('document:keydown.escape')
    onKeydown_dismiss(): void {
        if (this.guideService.active()) this.guideService.dismiss();
    }

    onClick_next(): void {
        this.guideService.next();
    }

    onClick_back(): void {
        this.guideService.back();
    }

    onClick_close(): void {
        this.guideService.dismiss();
    }

    ngOnDestroy(): void {
        this._stopTimer();
    }

    private _begin(): void {
        if (this._timer) return;

        this._returnFocus = this.document.activeElement as HTMLElement | null;
        this.ngZone.runOutsideAngular(() => {
            this._timer = setInterval(() => this._tick(), TICK_MS);
        });
    }

    private _end(): void {
        this._stopTimer();
        this._stepKey = '';
        this.spotlight.set(null);
        this.cardPosition.set(null);
        this.shown.set(null);
        this.mode.set('locating');
        this._returnFocus?.focus?.();
        this._returnFocus = null;
    }

    private _stopTimer(): void {
        if (this._timer) clearInterval(this._timer);
        this._timer = null;
    }

    private _tick(): void {
        const active = this.guideService.active();
        const step = this.guideService.step();
        if (!active || !step) return;

        const key = `${active.guide.id}:${active.index}`;
        if (key !== this._stepKey) {
            this._direction = active.index >= this._lastIndex ? 1 : -1;
            this._lastIndex = active.index;
            // The previous step stays on screen until this one is located, then both move at once
            this._stepKey = key;
            this._missingMs = 0;
        }

        const element = step.target
            ? this.document.querySelector<HTMLElement>(step.target)
            : null;
        const rect = element?.getBoundingClientRect();
        const present = !!rect && rect.width > 0 && rect.height > 0;

        if (!step.target) {
            this._show('centered', null);
        } else if (present) {
            this._missingMs = 0;
            this._revealIfOffscreen(element!, rect!);
            this._show('spotlight', this._padded(rect!));
        } else {
            this._missingMs += TICK_MS;
            this._onMissing(step.optional);
        }
    }

    private _direction: 1 | -1 = 1;

    private _onMissing(optional?: boolean): void {
        if (this._missingMs < LOCATE_MS) return;

        if (optional) {
            this.ngZone.run(() => this.guideService.skipMissingStep(this._direction));
        } else {
            this._show('centered', null);
        }
    }

    private _show(mode: Mode, box: Box | null): void {
        const changedMode = this.mode() !== mode;
        const previous = this.spotlight();
        const moved = box?.top !== previous?.top || box?.left !== previous?.left
            || box?.width !== previous?.width || box?.height !== previous?.height;

        const active = this.guideService.active();
        const step = this.guideService.step();

        this.ngZone.run(() => {
            if (changedMode) this.mode.set(mode);
            if (moved) this.spotlight.set(box);
            if (active && step && this.shown()?.step !== step) {
                this.shown.set({ index: active.index, step });
            }
            this.guideService.stepShown();
        });
        this._place(box);
        if (changedMode) setTimeout(() => this._focusCard());
    }

    private _place(box: Box | null): void {
        const card = this._card()?.nativeElement;
        const view = this.document.defaultView;
        if (!card || !view) return;

        const size = { width: card.offsetWidth, height: card.offsetHeight };
        const viewport = { width: view.innerWidth, height: view.innerHeight };
        const step = this.guideService.step();
        // Narrow screens use CSS for a bottom sheet, so there is nothing to place
        const centred = {
            top: (viewport.height - size.height) / 2,
            left: (viewport.width - size.width) / 2
        };
        const floating = box ? computeCardPosition(box, size, viewport, step?.placement) : centred;
        const position = viewport.width < NARROW_VIEWPORT ? null : floating;
        const current = this.cardPosition();
        if (current?.top !== position?.top || current?.left !== position?.left) {
            this.ngZone.run(() => this.cardPosition.set(position));
        }
    }

    private _revealIfOffscreen(element: HTMLElement, rect: DOMRect): void {
        const view = this.document.defaultView;
        const height = view?.innerHeight ?? 0;
        // On a bottom sheet the target must also sit above the card, not just on screen
        const sheet = (view?.innerWidth ?? NARROW_VIEWPORT) < NARROW_VIEWPORT
            ? this._card()?.nativeElement.offsetHeight ?? 0
            : 0;
        if (this._revealedFor === this._stepKey
            || (rect.top >= 0 && rect.bottom <= height - sheet)) {
            return;
        }

        this._revealedFor = this._stepKey;
        const reduced = view?.matchMedia('(prefers-reduced-motion: reduce)').matches;
        element.scrollIntoView({
            block: 'center',
            behavior: reduced ? 'auto' : 'smooth'
        });
    }

    private _revealedFor = '';

    private _padded(rect: DOMRect): Box {
        return {
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2
        };
    }

    private _focusCard(): void {
        this._card()?.nativeElement.focus({ preventScroll: true });
    }
}
